import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { BodyText, HStack, theme, useResponsive, VStack } from '@sg/garnish';

import { SweetpassBenefitCard } from '@order/components';

import type {
  SweetpassBenefitList,
  SweetpassBenefitsPageBenefitCtaHandler,
} from './hooks';

export const SweetpassBenefitsPageBenefitsContent = ({
  content,
  ctaHandler,
}: SweetpassBenefitsPageBenefitsContentProps) => {
  const { currentBreakpoint, match } = useResponsive();
  const DynamicStack = currentBreakpoint.isXS ? VStack : HStack;
  const areBenefitsUnavailable = content.palette === 'greyed-out';

  const gridSpacing = match([0, theme.spacing['6'], theme.spacing['10']]);

  return (
    <View style={styles.container}>
      <BodyText size={1} style={styles.benefitTitle}>
        {content.title}
      </BodyText>
      <DynamicStack
        testID={`sweetpass-benefits-page-benefits-list-wrapper${
          currentBreakpoint.isXS ? '-mobile' : ''
        }`}
        customSpacing={gridSpacing}
        itemsPerRow={3}
        itemStyle={styles.alignItemsCenter}
        dividerColor={theme.colors.LIGHT}
        divider
      >
        {content.benefits.map((benefit) =>
          benefit.action ? (
            <Pressable
              testID={`sweetpass-benefits-page-benefit-${benefit.id}-cta`}
              key={benefit.id}
              accessibilityRole="button"
              onPress={() => {
                ctaHandler(benefit.action);
              }}
            >
              <SweetpassBenefitCard
                title={benefit.titleText}
                text={benefit.bodyText}
                imgA11yLabel={benefit.image.description}
                imgUrl={benefit.image.url}
              />
            </Pressable>
          ) : (
            <SweetpassBenefitCard
              key={benefit.id}
              title={benefit.titleText}
              text={benefit.bodyText}
              imgA11yLabel={benefit.image.description}
              imgUrl={benefit.image.url}
              isUnavailable={areBenefitsUnavailable}
            />
          ),
        )}
      </DynamicStack>
    </View>
  );
};

type SweetpassBenefitsPageBenefitsContentProps = {
  readonly content: SweetpassBenefitList;
  readonly ctaHandler: SweetpassBenefitsPageBenefitCtaHandler;
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: theme.spacing['10'],
  },
  alignItemsCenter: {
    alignItems: 'center',
  },
  benefitTitle: {
    paddingBottom: theme.spacing['2'],
    marginBottom: theme.spacing['4'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.NEUTRAL_3,
    color: theme.colors.GREEN_1,
  },
});
